/**
 * VIBE_CODING_STANDARD
 *
 * -----
 * VERSION_HISTORY:
 * 1.0.0:gemini-1.5-flash (PREV) - Initial creation of the useWhitelabel hook.
 * 1.1.0:gemini-1.5-flash (CURRENT) - Implemented logic to prioritize whitelabel configuration
 *                                   received from an `X-White-Label` HTTP header over IndexedDB.
 *                                   Added `isHeaderConfigActive` state and registered a global
 *                                   handler for header updates. Disabled `updateWhitelabelConfig`
 *                                   and `resetWhitelabelConfig` if header config is active.
 *
 * -----
 * SECURITY_RISK: LOW - This hook manages UI whitelabel configuration from IndexedDB and HTTP headers.
 *                      It does not handle sensitive user data or API keys directly.
 * INTENT: Provide a centralized and reusable hook for managing the application's
 *         whitelabel configuration, including loading from and saving to IndexedDB,
 *         with precedence given to server-provided configurations via HTTP headers.
 * PROMPTER_INSTRUCTIONS: Ensure the hook correctly loads and persists configuration,
 *                        and handles loading/error states gracefully. Verify that header-provided
 *                        configs correctly override local storage and disable editing.
 * LLM_CONFIDENCE: 100% - Standard React hook implementation for state management and IndexedDB interaction,
 *                        with added logic for header-based overrides.
 */
import { useState, useEffect, useCallback } from 'react';
import { WhitelabelConfig } from '../types/whitelabel.d.ts';
import { getWhitelabelConfig, saveWhitelabelConfig } from '../utils/whitelabelConfig.ts';
import { registerWhitelabelHeaderHandler } from '../utils/whitelabelHeaderHandler.ts'; // NEW: Import the handler registration

interface UseWhitelabelResult {
  whitelabelConfig: WhitelabelConfig;
  loading: boolean;
  error: string | null;
  isHeaderConfigActive: boolean; // NEW: Indicates if the config is from an HTTP header
  updateWhitelabelConfig: (newConfig: Partial<WhitelabelConfig>) => Promise<void>;
  resetWhitelabelConfig: () => Promise<void>;
}

export const useWhitelabel = (): UseWhitelabelResult => {
  const [whitelabelConfig, setWhitelabelConfig] = useState<WhitelabelConfig>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [whitelabelLoaded, setWhitelabelLoaded] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [headerConfig, setHeaderConfig] = useState<WhitelabelConfig | null>(null); // NEW: State for config from header
  const [isHeaderConfigActive, setIsHeaderConfigActive] = useState<boolean>(false); // NEW: Flag for header config

  // NEW: Callback to update config from header
  const updateConfigFromHeader = useCallback((config: WhitelabelConfig | null) => {
    if (config) {
      setHeaderConfig(config);
      setIsHeaderConfigActive(true);
      setWhitelabelConfig(config); // Immediately apply header config
      setError(null); // Clear any previous errors
    } else {
      // If header is not present or invalid, revert to IndexedDB config
      setHeaderConfig(null);
      setIsHeaderConfigActive(false);
      loadConfig(); // Reload from IndexedDB  
    }
  }, []);

  // Register the header handler on mount
  useEffect(() => {
    if(!whitelabelLoaded) {
      setWhitelabelLoaded(true);
      registerWhitelabelHeaderHandler(updateConfigFromHeader);
    }
  }, [updateConfigFromHeader,  whitelabelLoaded, setWhitelabelLoaded]);


  const loadConfig = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // If header config is active, use it and skip IndexedDB
      const config = await getWhitelabelConfig();
      if(config) {
        setWhitelabelConfig(config);
      }
      
    } catch (err: any) {
      console.error("Failed to load whitelabel config:", err);
      //setError(err.message || "Failed to load whitelabel configuration.");
      //setWhitelabelConfig({}); // Fallback to empty config on error
      //setIsHeaderConfigActive(false);
    } finally {
      setLoading(false);
    }
  }, [headerConfig]); // Depend on headerConfig to re-evaluate if it becomes available

  useEffect(() => {
    if(!whitelabelLoaded) {
      setWhitelabelLoaded(true);
      loadConfig();
    }
  }, [loadConfig, whitelabelLoaded, setWhitelabelLoaded]);

  const updateWhitelabelConfig = useCallback(async (newConfig: Partial<WhitelabelConfig>) => {
    if (isHeaderConfigActive) {
      console.warn("Cannot update whitelabel config: it is currently overridden by an API header.");
      setError("Configuration is managed by the server and cannot be updated locally.");
      return;
    }
    setError(null);
    try {
      await saveWhitelabelConfig(newConfig);
      setWhitelabelConfig((prev) => ({ ...prev, ...newConfig }));
    } catch (err: any) {
      console.error("Failed to save whitelabel config:", err);
      setError(err.message || "Failed to save whitelabel configuration.");
    }
  }, [isHeaderConfigActive]);

  const resetWhitelabelConfig = useCallback(async () => {
    if (isHeaderConfigActive) {
      console.warn("Cannot reset whitelabel config: it is currently overridden by an API header.");
      setError("Configuration is managed by the server and cannot be reset locally.");
      return;
    }
    setError(null);
    try {
      await saveWhitelabelConfig({}); // Save an empty config to reset
      setWhitelabelConfig({}); // Reset local state
    } catch (err: any) {
      console.error("Failed to reset whitelabel config:", err);
      setError(err.message || "Failed to reset whitelabel configuration.");
    }
  }, [isHeaderConfigActive]);

  return {
    whitelabelConfig,
    loading,
    error,
    isHeaderConfigActive, // NEW: Expose the flag
    updateWhitelabelConfig,
    resetWhitelabelConfig,
  };
};
